import React, { cloneElement, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import usePlacement from './usePlacement/usePlacement';
import { PLACEMENT } from './usePlacement/constants';
import useKeyPress from 'react-use/lib/useKeyPress';
import { getTheme } from './utils';
import { alto } from '../Colors/Colors';
export default function Tooltip(_ref) {
  let {
    id,
    className,
    inline = false,
    message,
    placement = PLACEMENT.above,
    children,
    arrowSize = 8,
    border = 1,
    offset = 2,
    title,
    context = 'important',
    width = 'auto',
    displayTooltip,
    disablePadding
  } = _ref;
  const theme = useTheme();
  const tooltipRef = useRef(null);
  const tooltipAnchorRef = useRef(null);
  const [_displayTooltip, setDisplayTooltip] = useState(!!displayTooltip);
  const [isFocused, setIsFocused] = useState(false);
  const [tooltipCSS, setTooltip] = usePlacement();
  const classNames = [context];
  const escapeKeyEvent = useKeyPress('Escape');
  const enterKeyPressedEvent = useKeyPress('Enter');
  const spaceBarKeyEvent = useKeyPress(' ');
  useEffect(() => {
    const {
      titleBackground = '',
      messageBackground
    } = getTheme(context, theme, !!title);
    setTooltip(tooltipAnchorRef.current, tooltipRef.current, placement, arrowSize, border, offset, title, titleBackground, message, messageBackground);
  }, [placement, arrowSize, title, message, context]);
  useEffect(() => {
    const hasContent = (!!title || !!message) && placement !== PLACEMENT.none;
    displayTooltip === undefined && setDisplayTooltip(hasContent && isFocused);
  }, [isFocused]);
  useEffect(() => {
    displayTooltip === undefined && checkToggleTooltipKeyEvent(enterKeyPressedEvent);
  }, [enterKeyPressedEvent]);
  useEffect(() => {
    displayTooltip === undefined && checkToggleTooltipKeyEvent(spaceBarKeyEvent);
  }, [spaceBarKeyEvent]);
  useEffect(() => {
    if (isFocused) {
      const [isKeyPressed] = escapeKeyEvent; // meaning the key up is fired

      if (!isKeyPressed) {
        setDisplayTooltip(false);
      }
    }
  }, [escapeKeyEvent]);
  useEffect(() => {
    if (displayTooltip !== undefined) {
      setDisplayTooltip(displayTooltip);
    }
  }, [displayTooltip]);

  const checkToggleTooltipKeyEvent = keyEvent => {
    if (isFocused) {
      const [isKeyPressed] = keyEvent; // meaning the key up is fired

      if (!isKeyPressed) {
        setDisplayTooltip(!_displayTooltip);
      }
    }
  };

  if (title) classNames.push('titled');
  return /*#__PURE__*/React.createElement(TooltipContainer, {
    ref: tooltipAnchorRef,
    className: className || '',
    inline: inline,
    onMouseEnter: () => {
      displayTooltip === undefined && PLACEMENT.none !== placement && setDisplayTooltip(true);
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onMouseLeave: () => displayTooltip === undefined && setDisplayTooltip(false)
  }, React.Children.map(children, child => {
    return /*#__PURE__*/cloneElement(child, {
      id,
      'aria-describedby': "".concat(id, "-description"),
      onFocus: () => setIsFocused(true)
    });
  }), (title || message) && /*#__PURE__*/React.createElement(StyledTooltip, {
    ref: tooltipRef,
    id: "".concat(id, "-description"),
    className: classNames.join(' '),
    "aria-hidden": !_displayTooltip,
    show: _displayTooltip,
    border: border,
    context: context,
    role: "tooltip",
    titled: !!title,
    disablePadding: disablePadding || false,
    placement: placement,
    width: width,
    tooltipCSS: tooltipCSS,
    onMouseEnter: () => displayTooltip === undefined && setDisplayTooltip(false)
  }, title && /*#__PURE__*/React.createElement(Title, {
    context: context,
    titled: !!title
  }, title), /*#__PURE__*/React.createElement("div", null, message)));
}
const TooltipContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Tooltip__TooltipContainer",
  componentId: "sc-1w7dmer-0"
})(["display:", ";border-radius:inherit;"], _ref2 => {
  let {
    inline
  } = _ref2;
  return inline ? 'inline-block' : 'block';
});
const StyledTooltip = /*#__PURE__*/styled.div.withConfig({
  displayName: "Tooltip__StyledTooltip",
  componentId: "sc-1w7dmer-1"
})(["position:absolute;width:", ";max-width:100vw;background:", ";border-radius:5px;color:", ";font-size:13px;line-height:1.4;text-align:", ";border:", ";box-shadow:", ";", " visibility:", ";opacity:", ";transition:", ";transition-delay:", ";> div{", "}"], _ref3 => {
  let {
    width
  } = _ref3;
  return "".concat(width);
}, _ref4 => {
  let {
    context,
    theme,
    titled
  } = _ref4;
  return getTheme(context, theme, titled).messageBackground;
}, _ref5 => {
  let {
    context,
    theme,
    titled
  } = _ref5;
  return getTheme(context, theme, titled).messageText;
}, _ref6 => {
  let {
    titled
  } = _ref6;
  return titled ? 'left' : 'center';
}, _ref7 => {
  let {
    border,
    context,
    theme,
    titled
  } = _ref7;
  return "".concat(border, "px solid ").concat(getTheme(context, theme, titled).border);
}, _ref8 => {
  let {
    theme
  } = _ref8;
  return '0 3px 5px 0 ' + (theme === null || theme === void 0 ? void 0 : theme.boxShadowColor) || alto;
}, _ref9 => {
  let {
    tooltipCSS
  } = _ref9;
  return tooltipCSS;
}, _ref10 => {
  let {
    show
  } = _ref10;
  return show ? 'visible' : 'hidden';
}, _ref11 => {
  let {
    show
  } = _ref11;
  return show ? '1' : '0';
}, _ref12 => {
  let {
    show
  } = _ref12;
  return show ? 'visibility 0.25s, opacity 0.25s ease' : '0';
}, _ref13 => {
  let {
    show
  } = _ref13;
  return show ? '0.25s' : '';
}, _ref14 => {
  let {
    disablePadding
  } = _ref14;
  return disablePadding ? 'padding: 0' : 'padding: 7px 14px;';
});
const Title = /*#__PURE__*/styled.div.withConfig({
  displayName: "Tooltip__Title",
  componentId: "sc-1w7dmer-2"
})(["font-weight:700;white-space:nowrap;border-radius:5px 5px 0 0;background-color:", ";color:", ";"], _ref15 => {
  let {
    context,
    theme,
    titled
  } = _ref15;
  return getTheme(context, theme, titled).titleBackground;
}, _ref16 => {
  let {
    context,
    theme,
    titled
  } = _ref16;
  return getTheme(context, theme, titled).titleText;
});